import { Typography } from '@mui/material'
import { postStat } from 'api/stats/stats.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import moment, { Moment } from 'moment'
import React, { ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from 'ui/atoms/Button/Button'
import FollowupButton from 'ui/components/Followup/FollowupHeader/FollowupButton'
import { SettlementExportButton } from 'ui/components/Followup/SettlementExportButton/SettlementExportButton'
import { SettlementOptimizeButton } from 'ui/components/Followup/SettlementOptimizeButton/SettlementOptimizeButton'
import type { Props as DatePickerPeriodProps } from 'ui/molecules/pickers/DatePicker/DatePickerPeriod'
import { DatePickerPeriod } from 'ui/molecules/pickers/DatePicker/DatePickerPeriod'
import { handlePeriodChange } from 'views/FollowupProductionView/FollowupProductionView'

import styles from './HeaderPeriodPicker.module.less'

type HeaderPeriodPickerProps = {
  period: Period
  handlePeriodChange: handlePeriodChange
  loading?: boolean
  isFollowup?: boolean
  showTitle?: boolean
  onExport?: () => void
  exportButtonTitle?: string
  maxDate?: DatePickerPeriodProps['maxDate']
  minDate?: DatePickerPeriodProps['minDate']
  view?: string
}

export default function HeaderPeriodPicker({
  handlePeriodChange,
  loading,
  period,
  isFollowup,
  showTitle = true,
  onExport,
  exportButtonTitle,
  maxDate,
  minDate,
}: HeaderPeriodPickerProps): ReactElement {
  const { t } = useTranslation()

  useEffect(() => {
    setPeriodData({ period: { ...period } })
  }, [period])

  const [periodData, setPeriodData] = useState<{
    period: {
      startTime: Moment
      endTime: Moment
    }
  }>({
    period: {
      startTime: period.startTime,
      endTime: period.endTime,
    },
  })
  const [isError, setIsError] = useState(false)

  const handleDatePickerError = (error: boolean): void => {
    setIsError(error)
  }
  const hasFollowupBtnAccess = useHasPermission(`run_followup_opt`)

  return (
    <div>
      {showTitle && (
        <Typography variant="h3" className={styles.HeaderPeriodPicker_Title}>
          {t(`Choose period`)}
        </Typography>
      )}
      <div className={styles.HeaderPeriodPicker_Controls}>
        <div className={styles.HeaderPeriodPicker_DatePicker}>
          <DatePickerPeriod
            minDate={minDate}
            maxDate={maxDate ?? moment().endOf('day')}
            startDate={periodData.period.startTime}
            endDate={periodData.period.endTime}
            onSelect={(startTime, endTime) => {
              setPeriodData({
                period: {
                  startTime: moment(startTime),
                  endTime: moment(endTime),
                },
              })
            }}
            onError={handleDatePickerError}
          />
        </div>
        <div className={styles.HeaderPeriodPicker_Actions}>
          <div className={styles.HeaderPeriodPicker_LoadPeriodGroup}>
            <Button
              primary
              noRadiusLeft={isFollowup && hasFollowupBtnAccess}
              disabled={isError || loading}
              onClick={() => {
                handlePeriodChange(periodData.period)
                postStat(`period-picker`, `apply-period-change`)
              }}
            >
              {t(`Load period`)}
            </Button>
            {isFollowup && hasFollowupBtnAccess ? <FollowupButton period={periodData.period} /> : null}
          </div>
          {isFollowup ? <SettlementOptimizeButton period={periodData.period} /> : null}
          {isFollowup ? <SettlementExportButton period={periodData.period} /> : null}
          {onExport && (
            <Button marginLeft secondary icon="fas fa-cloud-download-alt" onClick={onExport}>
              {exportButtonTitle}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
